import { motion, AnimatePresence } from 'framer-motion';
import { Card } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Star, TrendingUp, Trophy, Zap, Flame, Target } from 'lucide-react';
import confetti from 'canvas-confetti';
import { useEffect } from 'react';

interface XPBreakdown {
  base: number;
  win: number;
  mvp: number;
  efficiency: number;
  streak: number;
  challenges: number;
  total: number;
}

interface XPRewardProps {
  breakdown: XPBreakdown;
  previousLevel: number;
  newLevel: number;
  leveledUp: boolean;
  levelProgress: number;
  onComplete?: () => void;
}

export default function XPReward({ 
  breakdown, 
  previousLevel, 
  newLevel, 
  leveledUp, 
  levelProgress,
  onComplete 
}: XPRewardProps) {
  
  useEffect(() => {
    if (leveledUp) {
      // Trigger level up celebration
      setTimeout(() => {
        confetti({
          particleCount: 150,
          spread: 100,
          origin: { y: 0.5 },
          colors: ['#10b981', '#3b82f6', '#8b5cf6', '#f59e0b', '#ef4444']
        });
      }, 500);
    }
  }, [leveledUp]);

  const breakdownItems = [
    { label: 'Base XP', value: breakdown.base, icon: Star, show: breakdown.base > 0 },
    { label: 'Win Bonus', value: breakdown.win, icon: Trophy, show: breakdown.win > 0 },
    { label: 'MVP Bonus', value: breakdown.mvp, icon: Zap, show: breakdown.mvp > 0 },
    { label: 'Efficiency', value: breakdown.efficiency, icon: TrendingUp, show: breakdown.efficiency > 0 },
    { label: 'Streak Bonus', value: breakdown.streak, icon: Flame, show: breakdown.streak > 0 },
    { label: 'Challenges', value: breakdown.challenges, icon: Target, show: breakdown.challenges > 0 },
  ].filter(item => item.show);

  return (
    <Card className="glass-card p-6 space-y-4 overflow-hidden">
      {/* Level Up Banner */}
      <AnimatePresence>
        {leveledUp && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8, y: -20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8, y: -20 }}
            className="bg-gradient-to-r from-primary/20 via-primary/30 to-primary/20 rounded-xl p-4 text-center border border-primary/50"
          >
            <motion.div
              animate={{ scale: [1, 1.2, 1] }}
              transition={{ duration: 0.5, repeat: 2 }}
              className="text-4xl mb-2"
            >
              🎉
            </motion.div>
            <h3 className="text-2xl font-black text-primary">LEVEL UP!</h3>
            <p className="text-lg font-bold">
              Level {previousLevel} → Level {newLevel}
            </p>
          </motion.div>
        )}
      </AnimatePresence>

      {/* XP Earned Header */}
      <div className="text-center">
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: 'spring', bounce: 0.5 }}
          className="inline-block"
        >
          <span className="text-5xl font-black text-primary">+{breakdown.total}</span>
          <span className="text-2xl font-bold text-muted-foreground ml-2">XP</span>
        </motion.div>
      </div>

      {/* XP Breakdown */}
      <div className="space-y-2">
        {breakdownItems.map((item, index) => (
          <motion.div
            key={item.label}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: index * 0.1 }}
            className="flex items-center justify-between px-3 py-2 bg-secondary/30 rounded-lg"
          >
            <div className="flex items-center gap-2">
              <item.icon className="h-4 w-4 text-primary" />
              <span className="text-sm font-semibold">{item.label}</span>
            </div>
            <span className="font-bold text-primary">+{item.value}</span>
          </motion.div>
        ))}
      </div>

      {/* Level Progress */}
      <div className="space-y-2 pt-2 border-t border-border/30">
        <div className="flex items-center justify-between">
          <span className="text-sm font-bold text-muted-foreground">Level {newLevel}</span>
          <span className="text-sm font-bold text-primary">{Math.round(levelProgress)}%</span>
        </div>
        <Progress value={levelProgress} className="h-2" />
      </div>
    </Card>
  );
}